import { useCallback, useMemo } from "react"
import { useVenue } from "providers/venue"

const EM_DEPARTMENT_STORE_REF = "local-category-emdistrict-0-department-store"

const isDepartmentStoreCategory = (category) =>
  category?.properties?.reference === EM_DEPARTMENT_STORE_REF

export const useDepartmentStore = (departmentId: string) => {
  const { occupants, taxonomies } = useVenue()

  const departmentStoreCategoryRoot = useMemo(
    () =>
      taxonomies?.find(
        ({ properties }) =>
          properties?.reference === EM_DEPARTMENT_STORE_REF &&
          properties?.parent_id === null
      ),
    [taxonomies]
  )

  const departmentStoreCategories = useMemo(
    () =>
      taxonomies?.filter(
        ({ properties }) =>
          properties?.parent_id === departmentStoreCategoryRoot?.id
      ),
    [departmentStoreCategoryRoot?.id, taxonomies]
  )

  const transformOccupant = useCallback(
    (occupant) => {
      const { properties } = occupant

      const newParentCategories = departmentId
        ? properties?.local_categories?.filter((cat) => cat.id === departmentId)
        : properties?.local_parent_categories

      return {
        ...occupant,
        properties: {
          ...properties,
          local_parent_categories: newParentCategories,
        },
      }
    },
    [departmentId]
  )

  const isOccupantInDepartment = useCallback(
    (occupant) => {
      const { properties } = occupant

      if (departmentId) {
        return properties?.local_sub_categories?.some(
          (category) => category.id === departmentId
        )
      }

      return properties?.local_parent_categories?.some(
        isDepartmentStoreCategory
      )
    },
    [departmentId]
  )

  const departmentStoreOccupants = useMemo(() => {
    return occupants.filter(isOccupantInDepartment).map(transformOccupant)
  }, [isOccupantInDepartment, transformOccupant, occupants])

  return {
    occupants: departmentStoreOccupants,
    departmentStoreCategories,
  }
}
